import { memo } from 'react';

/**
 * The quiet "managed fleet" field behind the login page.
 *
 * A console that manages many tablets is the whole product, so the backdrop
 * says that rather than decorating: tiny device outlines scattered across the
 * empty margins, a few drifting dots between them, and a dashed link that
 * occasionally appears between two devices — or between a device and the
 * security node — then fades out again.
 *
 * The geometry is generated ONCE at module load from a fixed seed, not on
 * render. The login form re-renders on every keystroke, and re-randomising the
 * layout there would make the whole field jump while someone types a password.
 * A fixed seed also means the composition is art-directed: it looks the same on
 * every machine, so it can be checked once and trusted.
 *
 * Everything animates in CSS. No library, no canvas, no timers — the browser
 * runs these off the main thread, so a login page still costs nothing.
 */

const W = 1440;
const H = 900;

/** The sign-in tablet column. Nothing is placed under the form. */
const CARD = { x0: 940, y0: 60, x1: W, y1: H };
/** The marketing copy. Kept sparse so text stays clean. */
const TEXT = { x0: 30, y0: 180, x1: 680, y1: 740 };

/** Where the security node sits: the gap between the copy and the card. */
const HUB = { x: 812, y: 452 };

function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const rnd = mulberry32(0x7ab1e7);
const between = (lo: number, hi: number) => lo + rnd() * (hi - lo);
const inside = (r: typeof CARD, x: number, y: number) =>
  x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1;

type Tone = 'blue' | 'navy' | 'green' | 'purple';

interface Device {
  x: number;
  y: number;
  w: number;
  h: number;
  tone: Tone;
  dx: number;
  dy: number;
  dur: number;
  delay: number;
  extra: boolean;
}

/** Mostly brand blue; green and purple appear just often enough to notice. */
function pickTone(): Tone {
  const r = rnd();
  if (r < 0.6) return 'blue';
  if (r < 0.85) return 'navy';
  if (r < 0.94) return 'green';
  return 'purple';
}

const devices: Device[] = [];
for (let attempt = 0; attempt < 400 && devices.length < 26; attempt += 1) {
  const x = between(40, W - 40);
  const y = between(60, H - 60);

  if (inside(CARD, x, y)) continue;
  // A few devices drift behind the copy, but only a few.
  if (inside(TEXT, x, y) && rnd() > 0.22) continue;
  if (devices.some((d) => Math.hypot(d.x - x, d.y - y) < 72)) continue;
  if (Math.hypot(HUB.x - x, HUB.y - y) < 70) continue;

  const portrait = rnd() > 0.4;
  const long = between(12, 16);
  const short = between(9, 11);

  devices.push({
    x,
    y,
    w: portrait ? short : long,
    h: portrait ? long : short,
    tone: pickTone(),
    dx: between(-13, 13),
    dy: between(-11, 11),
    dur: between(9, 18),
    delay: -between(0, 18),
    extra: rnd() > 0.55,
  });
}

interface Dot {
  x: number;
  y: number;
  r: number;
  tone: Tone;
  dx: number;
  dy: number;
  dur: number;
  delay: number;
  extra: boolean;
}

const dots: Dot[] = [];
for (let attempt = 0; attempt < 400 && dots.length < 38; attempt += 1) {
  const x = between(30, W - 30);
  const y = between(40, H - 40);

  if (inside(CARD, x, y)) continue;
  if (inside(TEXT, x, y) && rnd() > 0.3) continue;

  const size = rnd();
  dots.push({
    x,
    y,
    r: size > 0.88 ? 2.5 : size > 0.55 ? 1.5 : 1,
    tone: pickTone(),
    dx: between(-15, 15),
    dy: between(-13, 13),
    dur: between(10, 18),
    delay: -between(0, 18),
    extra: rnd() > 0.5,
  });
}

interface Link {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  dur: number;
  delay: number;
  hub: boolean;
}

/**
 * Candidate links, then a staggered schedule over one shared cycle.
 *
 * Each link is visible for roughly two seconds out of a ~31s cycle and the
 * delays are spread evenly across it, so one connection is usually lit and
 * two only briefly overlap. That is the "fleet checking in" read; a permanent
 * mesh would just look like a wireframe.
 */
const pairs: Array<[number, number]> = [];
for (let i = 0; i < devices.length; i += 1) {
  for (let j = i + 1; j < devices.length; j += 1) {
    const d = Math.hypot(devices[i]!.x - devices[j]!.x, devices[i]!.y - devices[j]!.y);
    if (d > 95 && d < 290) pairs.push([i, j]);
  }
}
// Deterministic shuffle so the chosen pairs are spread over the whole field.
for (let i = pairs.length - 1; i > 0; i -= 1) {
  const j = Math.floor(rnd() * (i + 1));
  [pairs[i], pairs[j]] = [pairs[j]!, pairs[i]!];
}

const hubAnchors = [...devices]
  .map((d, i) => ({ i, d: Math.hypot(d.x - HUB.x, d.y - HUB.y) }))
  .sort((a, b) => a.d - b.d)
  .slice(0, 3);

const links: Link[] = [
  ...pairs.slice(0, 6).map(([i, j]) => ({
    x1: devices[i]!.x,
    y1: devices[i]!.y,
    x2: devices[j]!.x,
    y2: devices[j]!.y,
    hub: false,
  })),
  ...hubAnchors.map(({ i }) => ({
    x1: devices[i]!.x,
    y1: devices[i]!.y,
    x2: HUB.x,
    y2: HUB.y,
    hub: true,
  })),
].map((link, i, all) => ({
  ...link,
  dur: 31 + (i % 3),
  delay: -(i * (31 / all.length)),
}));

function LoginBackdropInner() {
  return (
    <svg
      className="login-net"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
      focusable="false"
    >
      <g className="net-links">
        {links.map((l, i) => (
          <g key={`l${i}`} className={l.hub ? 'net-link-hub' : undefined}>
            <line
              className="net-link"
              x1={l.x1}
              y1={l.y1}
              x2={l.x2}
              y2={l.y2}
              vectorEffect="non-scaling-stroke"
              style={{ '--dur': `${l.dur}s`, '--delay': `${l.delay}s` } as React.CSSProperties}
            />
            <circle
              className="net-pulse"
              cx={l.x1}
              cy={l.y1}
              r={2}
              style={
                {
                  '--dur': `${l.dur}s`,
                  '--delay': `${l.delay}s`,
                  '--lx': `${l.x2 - l.x1}px`,
                  '--ly': `${l.y2 - l.y1}px`,
                } as React.CSSProperties
              }
            />
          </g>
        ))}
      </g>

      <g className="net-dots">
        {dots.map((d, i) => (
          <circle
            key={`d${i}`}
            className={`net-dot net-${d.tone}${d.extra ? ' net-extra' : ''}`}
            cx={d.x}
            cy={d.y}
            r={d.r}
            style={
              {
                '--dx': `${d.dx}px`,
                '--dy': `${d.dy}px`,
                '--dur': `${d.dur}s`,
                '--delay': `${d.delay}s`,
              } as React.CSSProperties
            }
          />
        ))}
      </g>

      <g className="net-devices">
        {devices.map((d, i) => (
          <g key={`t${i}`} transform={`translate(${d.x} ${d.y})`}>
            <g
              className={`net-device net-${d.tone}${d.extra ? ' net-extra' : ''}`}
              style={
                {
                  '--dx': `${d.dx}px`,
                  '--dy': `${d.dy}px`,
                  '--dur': `${d.dur}s`,
                  '--delay': `${d.delay}s`,
                } as React.CSSProperties
              }
            >
              <rect
                x={-d.w / 2}
                y={-d.h / 2}
                width={d.w}
                height={d.h}
                rx={2}
                vectorEffect="non-scaling-stroke"
              />
              <line
                x1={-d.w / 2 + 2.5}
                y1={d.h / 2 - 2.2}
                x2={d.w / 2 - 2.5}
                y2={d.h / 2 - 2.2}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          </g>
        ))}
      </g>

      <g className="net-hub" transform={`translate(${HUB.x} ${HUB.y})`}>
        <circle className="net-hub-halo" r={26} />
        <circle className="net-hub-wave" r={11} vectorEffect="non-scaling-stroke" />
        <circle className="net-hub-ring" r={7} vectorEffect="non-scaling-stroke" />
        <circle className="net-hub-core" r={2.6} />
      </g>
    </svg>
  );
}

export const LoginBackdrop = memo(LoginBackdropInner);
